import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { postSearch } from "../../components/apis/music-api";
import { SearchResults } from "../../types/SearchResults";
import callApi from "../../components/call-api/call-api";
import { ArtistMessage } from "../../types/ArtistMessage";
import { Album } from "../../types/Album";
import TrackSearchComponent from "./TrackSearchComponent";
import ArtistSearchResult from "./ArtistSearchResult";
import AlbumSearchResult from "./AlbumSearchResult";
import { TrackSearchResult } from "../../types/TrackSearchResult";
import CompilationSearchResult from "./CompilationSearchResult";
import { Compilation } from "../../types/Compilation";
import CompilationTrackSearchComponent from "./CompilationTrackSearchComponent";
import { CompilationTrackSearchResult } from "../../types/CompilationTrackSearchResult";
import { Genre } from "../../types/Genre";
import GenreSearchResult from "./GenreSearchResult";
import SearchCategory from "./SearchCategory";
import { useSearchStore } from "../../components/store/store";

const Search = () => {
    const { searchText } = useSearchStore();
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ openGenre, setOpenGenre ] = useState(false);
    const [ openArtist, setOpenArtist ] = useState(false);
    const [ openAlbum, setOpenAlbum ] = useState(false);
    const [ openTrack, setOpenTrack ] = useState(false);
    const [ openCompilation, setOpenCompilation ] = useState(false);
    const [ openCompilationTrack, setOpenCompilationTrack ] = useState(false);
    const [ searchResults, setSearchResults ] = useState<SearchResults>();
    const CloseLimit = 24;

    const setAll = (value: boolean) => {
        setOpenGenre(value);
        setOpenArtist(value);
        setOpenAlbum(value);
        setOpenTrack(value);
        setOpenCompilation(value);
        setOpenCompilationTrack(value);
    }

    useEffect(() => {
        callApi<SearchResults>(postSearch(getAccessToken(), searchText ?? ''), setSearchResults);
    }, [searchText, getAccessToken]);

    useEffect(() => {
        setOpenGenre((searchResults?.tracks && searchResults.genres.length < CloseLimit) ?? false);
        setOpenArtist((searchResults?.tracks && searchResults.artists.length < CloseLimit) ?? false);
        setOpenAlbum((searchResults?.tracks && searchResults.albums.length < CloseLimit) ?? false);
        setOpenCompilation((searchResults?.tracks && searchResults.compilations.length < CloseLimit) ?? false);
        setOpenTrack((searchResults?.tracks && searchResults.tracks.length < CloseLimit) ?? false);
        setOpenCompilationTrack((searchResults?.tracks && searchResults.compilationTracks.length < CloseLimit) ?? false);
    }, [searchResults]);

    const getExceeded = () => {
        const results = [];
        searchResults?.genreLimitExceeded === true && results.push('Genres');
        searchResults?.artistLimitExceeded === true && results.push('Artists');
        searchResults?.albumLimitExceeded === true && results.push('Albums');
        searchResults?.compilationLimitExceeded === true && results.push('Compilations');
        searchResults?.trackLimitExceeded === true && results.push('Tracks');
        searchResults?.compilationTrackLimitExceeded === true && results.push('Compilation Tracks');
        return results.join(', ');
    }
    
    return (
        <Stack direction="column" spacing={2}>
            <Box>
                <Typography variant='h5'>Search results for "{searchText}"</Typography>
            </Box>
            <Box>
                <Typography variant='body1'>Search for artists, albums, tracks and more. Results are limited to 100 per category.</Typography>
            </Box>
                {(searchResults?.genres !== undefined ||
                    searchResults?.artists !== undefined ||
                    searchResults?.albums !== undefined ||
                    searchResults?.compilations !== undefined ||
                    searchResults?.tracks !== undefined ||
                    searchResults?.compilationTracks !== undefined) &&
                    <>
                        <Box sx={{ width: '140px' }}>
                            <Stack direction="row" spacing={1}>
                                <Box>
                                    <Button variant="contained" size="small" sx={{ minWidth: '0px', padding: '4px 8px' }} color="info" onClick={() => setAll(true)}>
                                        <FontAwesomeIcon fontSize="small" icon={faPlus} />&nbsp;All
                                    </Button>                    
                                </Box>
                                <Box>
                                    <Button variant="contained" size="small" sx={{ minWidth: '0px', padding: '4px 8px' }} color="info" onClick={() => setAll(false)}>
                                        <FontAwesomeIcon fontSize="small" icon={faMinus} />&nbsp;All
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                        <Box>
                            <Stack direction="column">
                                {(searchResults?.genreLimitExceeded === true ||
                                    searchResults?.artistLimitExceeded === true ||
                                    searchResults?.albumLimitExceeded === true ||
                                    searchResults?.compilationLimitExceeded === true ||
                                    searchResults?.trackLimitExceeded === true ||
                                    searchResults?.compilationTrackLimitExceeded === true) &&
                                    <>
                                        <Typography>Results limit exceeded in
                                            <Typography component="span" color="error"> {getExceeded()}</Typography>
                                        </Typography>
                                    </>
                                }
                            </Stack>
                        </Box>
                    </>
                }
                <SearchCategory
                    results={searchResults?.genres}
                    searchText={searchText}
                    categoryTitle="Genres"
                    limitExceeded={searchResults?.genreLimitExceeded}
                    open={openGenre}
                    setOpen={setOpenGenre}
                >
                    {searchResults?.genres?.map((genre: Genre, index: number) =>
                        <GenreSearchResult index={index} searchText={searchText} genre={genre} />
                    )}
                </SearchCategory>
                <SearchCategory
                    results={searchResults?.artists}
                    searchText={searchText}
                    categoryTitle="Artists"
                    limitExceeded={searchResults?.artistLimitExceeded}
                    open={openArtist}
                    setOpen={setOpenArtist}
                >
                    {searchResults?.artists?.map((artistMessage: ArtistMessage, index: number) =>
                        <ArtistSearchResult index={index} searchText={searchText} artistMessage={artistMessage} />
                    )}
                </SearchCategory>
                <SearchCategory
                    results={searchResults?.albums}
                    searchText={searchText}
                    categoryTitle="Albums"
                    limitExceeded={searchResults?.albumLimitExceeded}
                    open={openAlbum}
                    setOpen={setOpenAlbum}
                >
                    {searchResults?.albums?.map((album: Album, index: number) =>
                        <AlbumSearchResult index={index} searchText={searchText} album={album} />
                    )}
                </SearchCategory>
                <SearchCategory
                    results={searchResults?.compilations}
                    searchText={searchText}
                    categoryTitle="Compilations"
                    limitExceeded={searchResults?.compilationLimitExceeded}
                    open={openCompilation}
                    setOpen={setOpenCompilation}
                >
                    {searchResults?.compilations?.map((compilation: Compilation, index: number) =>
                        <CompilationSearchResult index={index} searchText={searchText} compilation={compilation} />
                    )}
                </SearchCategory>
                <SearchCategory
                    results={searchResults?.tracks}
                    searchText={searchText}
                    categoryTitle="Tracks"
                    limitExceeded={searchResults?.trackLimitExceeded}
                    open={openTrack}
                    setOpen={setOpenTrack}
                >
                    {searchResults?.tracks?.map((track: TrackSearchResult, index: number) =>
                        <TrackSearchComponent index={index} searchText={searchText} trackMessage={track} />
                    )}
                </SearchCategory>
                <SearchCategory
                    results={searchResults?.compilationTracks}
                    searchText={searchText}
                    categoryTitle="Compilation Tracks"
                    limitExceeded={searchResults?.compilationTrackLimitExceeded}
                    open={openCompilationTrack}
                    setOpen={setOpenCompilationTrack}
                >
                    {searchResults?.compilationTracks?.map((track: CompilationTrackSearchResult, index: number) =>
                        <CompilationTrackSearchComponent index={index} searchText={searchText} trackMessage={track} />
                    )}
                </SearchCategory>
        </Stack>
    );
}

export default Search;